import { apiFetch, ApiError, API_BASE } from './api';

export interface FileNodeRemote {
  name: string;
  path?: string;
  type: 'file' | 'folder';
  size?: string;
  readonly?: boolean;
  modificationKind?: 'added' | 'modified' | 'deleted' | null;
  children?: FileNodeRemote[];
}

export interface FileModificationRemote {
  path: string;
  type: 'added' | 'modified' | 'deleted';
  kind: 'file' | 'folder';
  size?: string;
  at: string;
  by: string;
}

// NOTE : API_BASE réutilisé depuis ./api pour rester sur VITE_DEPLOY_API_URL
// (le backend recube-cicd) et pas VITE_API_BASE_URL (panel Xbox).

function authHeader(): Record<string, string> {
  const token = localStorage.getItem('recube.token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const containerFilesService = {
  tree: (projectId: string, code: string) =>
    apiFetch<FileNodeRemote>(`/api/projects/${projectId}/containers/${code}/files`),

  // Le content peut être text/plain ou binaire — on gère les deux séparément.
  readText: async (projectId: string, code: string, filePath: string): Promise<string> => {
    const url = `${API_BASE}/api/projects/${projectId}/containers/${code}/files/content?path=${encodeURIComponent(filePath)}`;
    const res = await fetch(url, { headers: { Accept: 'text/plain', ...authHeader() } });
    if (res.status === 401) throw new ApiError(401, 'Session expirée');
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new ApiError(res.status, text || `HTTP ${res.status}`);
    }
    return res.text();
  },

  downloadBinary: (projectId: string, code: string, filePath: string) => {
    const url = `${API_BASE}/api/projects/${projectId}/containers/${code}/files/content?path=${encodeURIComponent(filePath)}`;
    const a = document.createElement('a');
    a.href = url + (url.includes('?') ? '&' : '?') + 't=' + Date.now();
    // download trigger via Authorization header impossible avec <a> simple → on fait un fetch+blob
    fetch(url, { headers: { ...authHeader() } })
      .then((r) => r.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filePath.split('/').pop() ?? 'download';
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(blobUrl);
      });
  },

  writeText: async (projectId: string, code: string, filePath: string, content: string): Promise<FileModificationRemote> => {
    const url = `${API_BASE}/api/projects/${projectId}/containers/${code}/files/content?path=${encodeURIComponent(filePath)}`;
    const res = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'text/plain', Accept: 'application/json', ...authHeader() },
      body: content,
    });
    if (res.status === 401) throw new ApiError(401, 'Session expirée');
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new ApiError(res.status, text || `HTTP ${res.status}`);
    }
    return res.json();
  },

  deletePath: (projectId: string, code: string, filePath: string) =>
    apiFetch<FileModificationRemote>(
      `/api/projects/${projectId}/containers/${code}/files/content?path=${encodeURIComponent(filePath)}`,
      { method: 'DELETE' }
    ),

  createFolder: (projectId: string, code: string, folderPath: string) =>
    apiFetch<FileModificationRemote>(
      `/api/projects/${projectId}/containers/${code}/files`,
      { method: 'POST', json: { type: 'folder', path: folderPath } }
    ),

  /** Renomme/déplace un fichier OU dossier (backend = ancien supprimé + nouveau ajouté). */
  rename: (projectId: string, code: string, fromPath: string, toPath: string) =>
    apiFetch<FileModificationRemote[]>(
      `/api/projects/${projectId}/containers/${code}/files/rename`,
      { method: 'POST', json: { from: fromPath, to: toPath } }
    ),

  upload: async (projectId: string, code: string, parentPath: string, file: File): Promise<FileModificationRemote> => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('path', parentPath);
    const url = `${API_BASE}/api/projects/${projectId}/containers/${code}/files`;
    const res = await fetch(url, { method: 'POST', headers: { ...authHeader() }, body: fd });
    if (res.status === 401) throw new ApiError(401, 'Session expirée');
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new ApiError(res.status, text || `HTTP ${res.status}`);
    }
    return res.json();
  },

  listMods: (projectId: string, code: string) =>
    apiFetch<FileModificationRemote[]>(`/api/projects/${projectId}/containers/${code}/files/modifications`),

  revertOne: (projectId: string, code: string, filePath: string) =>
    apiFetch<void>(
      `/api/projects/${projectId}/containers/${code}/files/modifications/revert`,
      { method: 'POST', json: { path: filePath } }
    ),

  revertAll: (projectId: string, code: string) =>
    apiFetch<void>(`/api/projects/${projectId}/containers/${code}/files/modifications`, { method: 'DELETE' }),
};
